<template lang="pug">
.container
    .chart-container(v-if="seriesData.length > 0")
        chart(:options="flowChartOptions")
        .modal-overlay(v-if="showTableModal" @click.self="showTableModal = false")
            .modal-content
                button.close(@click="showTableModal = false") ×
                h3 Series Data Table
                    v-icon.copy-btn(@click="copyTableToClipboard") content_copy
                table.table()
                    thead
                        tr
                            th Hour
                            th(v-for="series in seriesData" :key="series.name") {{ series.name }}
                    tbody
                        tr(v-for="(hour, idx) in 24" :key="idx")
                            td {{ formatHour(idx) }}
                            td(v-for="series in seriesData" :key="series.name")
                                | {{ series.data[idx] ?? '-' }}
    .no-data(v-else)
        v-card(light rounded class='mt-2')
            v-card-title(class="brand white--text") {{name || 'No Data Available'}}
            v-card-text No data available to display the chart.

</template>

<script lang="js">

import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import exportingInit from 'highcharts/modules/exporting';
import {cloneDeep} from 'lodash';

exportingInit(Highcharts);

export default {
    name: 'FlowChart',
    components: {
        chart: Chart
    },
    props: {
        seriesData: {
            type: Array,
            required: false,
            default: () => []
        },
        productionTime: {
            type: Number,
            required: false,
            default: null
        },
        productionVolume: {
            type: Number,
            required: false,
            default: null
        },
        startDate: {
            type: String,
            required: false,
            default: ''
        },
        name: {
            type: String,
            required: false,
        },
        isHistorical: {
            type: Boolean,
            required: false,
            default: false
        },
        productionVolumeUnit: {
            type: String,
            required: false,
            default: ''
        },
        rateUnit: {
            type: String,
            required: false,
            default: ''
        },
        yMaxRate: {
            type: Number,
            required: false,
            default: null
        },
        yMaxVolume: {
            type: Number,
            required: false,
            default: null
        },
        tooltipPositionSetting: {
            type: String,
            required: false,
            default: null
        }
    },
    data () {
        const highchartsColors = Highcharts.getOptions().colors;
        const vm = this;
        return {
            flowChartOptions: {
                credits: { enabled: false },
                colors: ['#058DC7', '#50B432', '#ED561B', '#754087', '#24CBE5', '#64E572'],
                lang: {
                    contextButtonTitle: 'Open chart menu'
                },
                chart: {
                    type: 'line',
                    backgroundColor: '#ffffff',
                    plotBorderWidth: 0,
                    shadow: {
                        color: 'rgba(0, 0, 0, 0.10)',
                        offsetX: 0,
                        offsetY: 4,
                        opacity: 1,
                        width: 10
                    },
                    spacing: [30, 30, 50, 30],
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: '#c8d6e5',
                    alignThresholds: true,
                    style: {
                        fontFamily: 'inherit'
                    }
                },
                title: {
                    text: '',
                    align: 'left'
                },
                yAxis: [
                    {
                        title: {
                            text: 'Rate',
                            style: { fontWeight: '700', color: '#555', fontSize: '12px' }
                        },
                        gridLineColor: '#edf0f5',
                        gridLineDashStyle: 'shortDash',
                        labels: {
                            style: { color: '#666', fontWeight: '600', fontSize: '11px' }
                        }
                    },
                    {
                        title: {
                            text: 'Volume',
                            style: { fontWeight: '700', color: '#555', fontSize: '12px' }
                        },
                        opposite: true,
                        gridLineWidth: 0,
                        labels: {
                            style: { color: '#666', fontWeight: '600', fontSize: '11px' }
                        }
                    },
                ],
                xAxis: {
                    categories: Array.from({ length: 24 }, (_, i) => i),
                    crosshair: {
                        width: 1.5,
                        color: 'rgba(0,0,0,0.10)',
                        dashStyle: 'shortDot'
                    },
                    gridLineWidth: 1,
                    gridLineColor: '#edf0f5',
                    labels: {
                        style: { color: '#666', fontWeight: '600', fontSize: '11px' },
                        formatter: function () {
                            let hour = this.value;
                            let amPm = hour < 12 ? 'AM' : 'PM';
                            hour = hour % 12;
                            hour = hour || 12;
                            return `${hour} ${amPm}`;
                        }
                    }
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.97)',
                    borderColor: '#d7e3f1',
                    borderRadius: 10,
                    shadow: true,
                    style: {
                        color: '#1a2a3a',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        let hour = this.x;
                        let amPm = hour < 12 ? 'AM' : 'PM';
                        hour = hour % 12;
                        hour = hour || 12;
                        let s = `<h3 style="margin: 2px 0; color:#1a2a3a; letter-spacing:0.03em;">Time: ${hour} ${amPm}</h3><br>`;
                        s += '<table style="width:100%; text-align: left;">';
                        this.points.forEach(function (point) {
                            let seriesName = point.series.name
                                .toLowerCase()
                                .split(' ')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                                .trim();

                            const isStorage = seriesName === 'Storage';
                            const uom = isStorage ? vm.productionVolumeUnit : vm.rateUnit;
                            const formatted = vm.formatNumberFromUnit(point.y, uom);
                            s += `<tr>` +
                                `<td>` +
                                    `<span style="color:${point.color}; font-size: 14px;"><strong>${seriesName}</strong></span>` +
                                `</td>` +
                                `<td style="text-align: right; padding-left: 10px;">` +
                                    `<b>${formatted}</b> <span style="color:#6f7f90;">${uom}</span>` +
                                `</td>` +
                            `</tr>`;
                        });
                        s += '</table>';
                        return s;
                    }
                },
                legend: {
                    layout: 'horizontal',
                    align: 'center',
                    verticalAlign: 'bottom',
                    backgroundColor: '#f8fafd',
                    borderRadius: 6,
                    borderWidth: 1,
                    borderColor: '#dce3ee',
                    shadow: false,
                    itemStyle: {
                        fontWeight: '600',
                        fontSize: '12px',
                        color: '#333'
                    },
                    itemHoverStyle: { color: '#000' },
                    labelFormatter: function () {
                        // Convert the series name to Title Case
                        return this.name
                            .toLowerCase()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                            .trim();
                    }
                },
                plotOptions: {
                    series: {
                        marker: {
                            enabled: false,
                            radius: 4,
                            lineWidth: 2,
                            lineColor: null
                        },
                        lineWidth: 2.5,
                        animation: {
                            duration: 900,
                            easing: 'easeOutQuart'
                        },
                        states: {
                            hover: {
                                lineWidthPlus: 1
                            }
                        }
                    }
                },
                series: [],
                exporting: {
                    fallbackToExportServer: false,
                    enabled: true,
                    buttons: {
                        contextButton: {
                            text: 'Menu',
                            symbol: 'menu',
                            symbolStroke: '#00355c',
                            symbolStrokeWidth: 2,
                            symbolX: 12,
                            symbolY: 12,
                            align: 'right',
                            y: -4,
                            x: -8,
                            theme: {
                                fill: '#e3f2fd',
                                stroke: '#1976d2',
                                'stroke-width': 2,
                                r: 4,
                                padding: 8,
                                style: {
                                    color: '#00355c',
                                    fontWeight: '700',
                                    fontSize: '12px'
                                },
                                states: {
                                    hover: {
                                        fill: '#bbdefb',
                                        style: {
                                            color: '#002a47'
                                        }
                                    },
                                    select: {
                                        fill: '#90caf9'
                                    }
                                }
                            },
                            menuItems: [
                                'viewFullscreen',
                                'printChart',
                                'separator',
                                {
                                    text: 'Show Data Table',
                                    onclick: () => {
                                        if (this.self) {
                                            this.self.showTableModal = true;
                                        }
                                    }
                                },
                                {
                                    text: 'Toggle Rate yMax',
                                    onclick: () => {
                                        if (this.self) {
                                            this.self.toggleYMax(0);
                                        }
                                    }
                                },
                                {
                                    text: 'Toggle Volume yMax',
                                    onclick: () => {
                                        if (this.self) {
                                            this.self.toggleYMax(1);
                                        }
                                    }
                                },
                                'separator',
                                {
                                    text: 'Tooltip Position: Auto',
                                    onclick: () => {
                                        if (this.self) {
                                            this.self.setTooltipPosition('auto');
                                        }
                                    }
                                },
                                {
                                    text: 'Tooltip Position: Below X-Axis',
                                    onclick: () => {
                                        if (this.self) {
                                            this.self.setTooltipPosition('below');
                                        }
                                    }
                                }
                            ]
                        }
                    }
                }
            },
            showTableModal: false,
            parsedStartDate: '',
            parsedStartDayOfWeek: '',
            tooltipPosition: 'auto',
            originalTooltipFormatter: null,
            self: this,
        };
    },
    watch: {
        seriesData: {
            handler (newVal, oldVal) {
                this.$set(this.flowChartOptions, 'series', newVal);
            },
            deep: true
        },
        productionTime () {
            this.updateSubtitle();
        },
        productionVolume () {
            this.updateSubtitle();
        },
        startDate () {
            this.updateSubtitle();
        },
        tooltipPositionSetting (newVal) {
            if (!newVal || newVal === this.tooltipPosition) {
                return;
            }
            this.applyTooltipPosition(newVal, { persist: false });
        }
    },
    methods: {
        parseStartDateParts (value = '') {
            const withoutTags = value.replaceAll(/<[^>]*>/g, '').trim();
            if (!withoutTags) {
                return {
                    date: '',
                    dayOfWeek: ''
                };
            }

            const normalized = withoutTags.replaceAll(/&raquo;|»/g, '|');
            const parts = normalized
                .split('|')
                .map(part => part.trim())
                .filter(Boolean);

            return {
                date: parts[0] || '',
                dayOfWeek: parts[1] || ''
            };
        },

        /**
         * Formats a number based on its unit.
         * @param {number} value
         * @param {string} unit
         */
        formatNumberFromUnit (value, unit) {
            const rawValue = value ?? 0;
            const normalizedUnit = (unit || '').trim().toLowerCase();
            switch (normalizedUnit) {
            case 'gal':
            case 'gpm':
                return Number(Math.round(rawValue)).toLocaleString();
            case 'gph':
                return Number(rawValue.toFixed(1)).toLocaleString();
            case 'gpd':
                return Number(rawValue.toFixed(2)).toLocaleString();
            default:
                return Number(rawValue.toFixed(3)).toLocaleString();
            }
        },

        init () {
            const titleAddendum = this.isHistorical ? '(actual)' : '(prediction)';
            this.flowChartOptions.title.text = `${this.name} ${titleAddendum}`;
            this.flowChartOptions.yAxis[0].max = this.yMaxRate;
            this.flowChartOptions.yAxis[1].max = null;
            if (this.seriesData?.length > 0) {
                this.$set(this.flowChartOptions, 'series', this.seriesData);
            } else {
                this.$set(this.flowChartOptions, 'series', []);
            }

            if (this.tooltipPositionSetting) {
                this.applyTooltipPosition(this.tooltipPositionSetting, { persist: false });
            } else {
                // Load tooltip position from localStorage
                try {
                    const storedPosition = localStorage.getItem('flowChartTooltipPosition');
                    if (storedPosition) {
                        this.setTooltipPosition(storedPosition);
                    }
                } catch (e) {
                    console.warn('Failed to load tooltip position from localStorage:', e);
                }
            }

            this.updateSubtitle();
        },
        updateSubtitle () {
            const { date, dayOfWeek } = this.parseStartDateParts(this.startDate);
            this.parsedStartDate = date;
            this.parsedStartDayOfWeek = dayOfWeek;

            let subtitleText = '';
            if (this.parsedStartDate && this.parsedStartDayOfWeek) {
                subtitleText = `Date: ${this.parsedStartDate} (${this.parsedStartDayOfWeek})`;
            } else if (this.parsedStartDate) {
                subtitleText = `Date: ${this.parsedStartDate}`;
            } else if (this.parsedStartDayOfWeek) {
                subtitleText = `Day: ${this.parsedStartDayOfWeek}`;
            }

            if (this.productionTime != null || this.productionVolume != null) {
                const formattedVolume = this.formatNumberFromUnit(this.productionVolume, this.productionVolumeUnit);
                subtitleText += `<br>Production Volume: <span style="font-weight:bolder;">${formattedVolume} ${this.productionVolumeUnit}</span>`;
            }
            this.flowChartOptions.subtitle = {
                text: subtitleText,
                align: 'left'
            };
        },

        formatHour (hour) {
            let amPm = hour < 12 ? 'AM' : 'PM';
            let h = hour % 12;
            h = h || 12;
            return `${h} ${amPm}`;
        },

        copyTableToClipboard () {
            // Start with the header row
            let rows = [];
            let header = ['Hour', ...this.seriesData.map(s => s.name)];
            rows.push(header);

            // Add each row of data
            for (let i = 0; i < 24; i++) {
                let row = [this.formatHour(i)];
                for (const series of this.seriesData) {
                    row.push(series.data[i] ?? '-');
                }
                rows.push(row);
            }

            // Convert to TSV (tab-separated)
            let tsv = rows.map(r => r.join('\t')).join('\n');

            // Copy to clipboard (async Clipboard API)
            navigator.clipboard.writeText(tsv)
                .then(() => {
                    this.$emit('notify', 'Copied table to clipboard!');
                })
                .catch(err => {
                    alert('Failed to copy: ' + err);
                });
        },

        toggleYMax (axisIndex = 0) {
            const yMax = axisIndex === 0 ? this.yMaxRate : this.yMaxVolume;
            if (this.flowChartOptions.yAxis[axisIndex].max === null) {
                // Enable yMax using the prop value
                this.flowChartOptions.yAxis[axisIndex].max = yMax;
            } else {
                // Disable yMax
                this.flowChartOptions.yAxis[axisIndex].max = null;
            }
            this.flowChartOptions = cloneDeep(this.flowChartOptions);
        },

        setTooltipPosition (position = 'auto') {
            this.applyTooltipPosition(position);
            this.$emit('tooltip-position-change', position);
        },

        applyTooltipPosition (position = 'auto', options = {}) {
            const { persist = true } = options;
            this.tooltipPosition = position;

            if (persist) {
                // Save to localStorage
                try {
                    localStorage.setItem('flowChartTooltipPosition', position);
                } catch (e) {
                    console.warn('Failed to save tooltip position to localStorage:', e);
                }
            }

            // Store original formatter on first call
            if (!this.originalTooltipFormatter) {
                this.originalTooltipFormatter = this.flowChartOptions.tooltip.formatter;
            }

            if (position === 'below') {
                // Position tooltip below the xAxis
                this.flowChartOptions.tooltip.positioner = function (labelWidth, labelHeight, point) {
                    // Get the axis width
                    const chart = this.chart;
                    const plotLeft = chart.plotLeft;
                    const plotTop = chart.plotTop;
                    const plotHeight = chart.plotHeight;
                    const plotBottom = plotTop + plotHeight;

                    // Position the tooltip below the x-axis with some padding
                    const x = Math.max(plotLeft, Math.min(point.plotX + plotLeft - labelWidth / 2, chart.chartWidth - labelWidth));
                    const y = plotBottom + 10;

                    return { x: x, y: y };
                };

                // Use a table layout so values align directly under their series headers.
                const formatNumberFromUnit = this.formatNumberFromUnit;
                const productionVolumeUnit = this.productionVolumeUnit;
                const rateUnit = this.rateUnit;
                this.flowChartOptions.tooltip.formatter = function () {
                    let hour = this.x;
                    let amPm = hour < 12 ? 'AM' : 'PM';
                    hour = hour % 12;
                    hour = hour || 12;
                    const preferredOrder = ['Demand', 'Production', 'Storage'];
                    const rows = [];

                    this.points.forEach(function (point) {
                        let seriesName = point.series.name
                            .toLowerCase()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                            .trim();

                        const isStorage = seriesName === 'Storage';
                        const uom = isStorage ? productionVolumeUnit : rateUnit;
                        const formatted = formatNumberFromUnit(point.y, uom);
                        rows.push({
                            seriesName,
                            color: point.color,
                            formatted,
                            uom
                        });
                    });

                    rows.sort((a, b) => {
                        const aIndex = preferredOrder.indexOf(a.seriesName);
                        const bIndex = preferredOrder.indexOf(b.seriesName);
                        const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
                        const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
                        return normalizedA - normalizedB;
                    });

                    const colCount = Math.max(rows.length, 1);
                    const headerCells = rows.map(row => (
                        `<th style="padding: 2px 10px; font-size: 13px; font-weight: 700; color:${row.color}; white-space: nowrap;">${row.seriesName}</th>`
                    )).join('');
                    const valueCells = rows.map(row => (
                        `<td style="padding: 0 10px 2px; text-align: center; white-space: nowrap;"><b>${row.formatted}</b> <span style="color:#6f7f90; font-size: 11px;">${row.uom}</span></td>`
                    )).join('');

                    return `<table style="border-collapse: collapse; margin: 0 auto; text-align: center;">` +
                        `<tr><td colspan="${colCount}" style="padding: 0 6px 4px;"><h3 style="margin: 2px 0; color:#1a2a3a; letter-spacing:0.03em;">Time: ${hour} ${amPm}</h3></td></tr>` +
                        `<tr>${headerCells}</tr>` +
                        `<tr>${valueCells}</tr>` +
                    `</table>`;
                };
            } else {
                // Auto positioning - let Highcharts handle it
                this.flowChartOptions.tooltip.positioner = null;

                // Restore original formatter
                this.flowChartOptions.tooltip.formatter = this.originalTooltipFormatter;
            }

            this.flowChartOptions = cloneDeep(this.flowChartOptions);
        },

    },
    mounted () {
        this.init();
    },
};

</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(10, 20, 40, 0.45);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.modal-content {
    background: #fff;
    padding: 28px 36px;
    border-radius: 12px;
    min-width: 380px;
    max-width: 90vw;
    max-height: 80vh;
    overflow: auto;
    position: relative;
    box-shadow: 0 12px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.10);
}
button.close {
    position: absolute;
    top: 10px;
    right: 14px;
    background: #f0f4f8;
    border: 1px solid #dce3ee;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2em;
    line-height: 1;
    cursor: pointer;
    transition: background 0.15s;
}
button.close:hover {
    background: #dce3ee;
}
.table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
}
.table th, .table td, h3 {
    border: 1px solid #ddd;
    padding: 6px 10px;
    text-align: center;
    color: #060b33;
}
.table th {
    background: #edf1f7;
    font-weight: 700;
    letter-spacing: 0.03em;
}
.copy-btn {
    background: #fff;
    border-radius: 5px;
    color: #222;
    padding: 5px 14px;
    font-size: 1em;
    cursor: pointer;
    margin-bottom: 8px;
    float: right;
}
.copy-btn:hover {
    background: rgb(197, 195, 195);
}

</style>
